import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useEffect, useState } from 'react';
import { FiCheckCircle, FiUploadCloud } from 'react-icons/fi';
import { ImportResolutionWizard } from '../ImportResolutionWizard';
import {
  ImportReconciliationStep,
  isConciliado,
  situacaoDoItem,
  type ReconciliationChoiceState,
  type ReconciliationItem,
  type ReconciliationOption,
  type ReconciliationSection,
} from '../ImportReconciliationStep';
import { Button, Card, Modal, toast } from '../../ui';
import { api } from '../../../services/api';
import { carregarProgresso, limparProgresso, limparProgressoAntigo, salvarProgresso } from '../progresso';
import type { ImportReconciliationMapping, ImportRowCorrection } from '../../../types/imports';
import styles from './ImportLancamentosModal.module.css';

type PreviewResponse = Awaited<ReturnType<typeof api.previewImportLancamentos>>;

/** Tudo que o usuário decidiu sobre um lote. Set vira array para caber em JSON. */
interface ProgressoDoLote {
  etapa: 0 | 1 | 2 | 3 | 4;
  escolhasGrupos: Record<string, ReconciliationChoiceState>;
  escolhasCategorias: Record<string, ReconciliationChoiceState>;
  subCategoryTypes: Record<string, 'ENTRADA' | 'SAIDA'>;
  keptDuplicateRowNumbers: number[];
  reviewedDuplicateRowNumbers: number[];
  skipAllDuplicates: boolean;
  ignoredRowNumbers: number[];
}

interface ImportLancamentosModalProps {
  onClose: () => void;
}

export function ImportLancamentosModal({ onClose }: ImportLancamentosModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isFixingRows, setIsFixingRows] = useState(false);
  const [isDownloadingErrors, setIsDownloadingErrors] = useState(false);
  const [isResuming, setIsResuming] = useState(true);
  const [resumed, setResumed] = useState(false);
  const [keptDuplicateRowNumbers, setKeptDuplicateRowNumbers] = useState<Set<number>>(new Set());
  const [reviewedDuplicateRowNumbers, setReviewedDuplicateRowNumbers] = useState<Set<number>>(new Set());
  const [skipAllDuplicates, setSkipAllDuplicates] = useState(false);
  const [ignoredRowNumbers, setIgnoredRowNumbers] = useState<Set<number>>(new Set());
  const [subCategoryTypes, setSubCategoryTypes] = useState<Record<string, 'ENTRADA' | 'SAIDA'>>({});
  // Etapa visível do wizard: 0 Arquivo, 1 Grupos, 2 Categorias, 3 Revisão, 4 Concluído.
  const [etapa, setEtapa] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [escolhasGrupos, setEscolhasGrupos] = useState<Record<string, ReconciliationChoiceState>>({});
  const [escolhasCategorias, setEscolhasCategorias] = useState<Record<string, ReconciliationChoiceState>>({});
  const [result, setResult] = useState<{ batchId: string; totalsCriados: number; dataMin?: string | null; dataMax?: string | null; entidadesCriadas: { categorias: number; subCategorias: number; formasPagamento: number }; subCategoriasReclassificadas?: number; skippedDuplicates: number } | null>(null);

  /** Devolve o modal ao estado em que o usuário parou. */
  const restaurar = (salvo: ProgressoDoLote) => {
    setEtapa(salvo.etapa);
    setEscolhasGrupos(salvo.escolhasGrupos ?? {});
    setEscolhasCategorias(salvo.escolhasCategorias ?? {});
    setSubCategoryTypes(salvo.subCategoryTypes ?? {});
    setKeptDuplicateRowNumbers(new Set(salvo.keptDuplicateRowNumbers ?? []));
    setReviewedDuplicateRowNumbers(new Set(salvo.reviewedDuplicateRowNumbers ?? []));
    setSkipAllDuplicates(Boolean(salvo.skipAllDuplicates));
    setIgnoredRowNumbers(new Set(salvo.ignoredRowNumbers ?? []));
  };

  useEffect(() => {
    let cancelled = false;
    api.getPendingImportLancamentos()
      .then((pending) => {
        if (!cancelled && pending) {
          setPreview(pending);
          setResumed(true);
          const salvo = carregarProgresso<ProgressoDoLote>(pending.batchPreviewId, 'modal');
          if (salvo) {
            restaurar(salvo);
          } else {
            setEtapa(pending.reconciliation && pending.reconciliation.grupos.length > 0 ? 1 : 3);
          }
        }
      })
      .catch(() => { /* sem lote pendente ou erro ao buscar — segue pro upload normal */ })
      .finally(() => { if (!cancelled) setIsResuming(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!preview) {
      setSubCategoryTypes({});
      return;
    }

    setSubCategoryTypes((current) => {
      const next = { ...current };
      for (const row of preview.indeterminate) {
        const key = row.normalizedKey || (row.subCategoriaOriginal || '').trim().toLowerCase();
        if (key && !next[key]) next[key] = 'SAIDA';
      }
      return next;
    });
  }, [preview]);

  // Grava a cada mudança. Fechar o modal sem querer deixa de custar o
  // trabalho de conciliar dezenas de categorias.
  useEffect(() => {
    if (!preview || result) return;
    salvarProgresso<ProgressoDoLote>(preview.batchPreviewId, 'modal', {
      etapa,
      escolhasGrupos,
      escolhasCategorias,
      subCategoryTypes,
      keptDuplicateRowNumbers: Array.from(keptDuplicateRowNumbers),
      reviewedDuplicateRowNumbers: Array.from(reviewedDuplicateRowNumbers),
      skipAllDuplicates,
      ignoredRowNumbers: Array.from(ignoredRowNumbers),
    });
  }, [
    preview,
    result,
    etapa,
    escolhasGrupos,
    escolhasCategorias,
    subCategoryTypes,
    keptDuplicateRowNumbers,
    reviewedDuplicateRowNumbers,
    skipAllDuplicates,
    ignoredRowNumbers,
  ]);

  const handleSetSubCategoryType = (key: string, tipo: 'ENTRADA' | 'SAIDA') => {
    setSubCategoryTypes((current) => ({ ...current, [key]: tipo }));
  };

  const handlePreview = async () => {
    if (!file) {
      toast('Selecione um arquivo .xlsx ou .xlsm', 'error');
      return;
    }

    setIsPreviewing(true);
    try {
      const response = await api.previewImportLancamentos(file);
      setPreview(response);
      setResult(null);
      setEscolhasGrupos({});
      setEscolhasCategorias({});
      setEtapa(response.reconciliation && response.reconciliation.grupos.length > 0 ? 1 : 3);
      limparProgressoAntigo(response.batchPreviewId);
      toast('Preview gerado com sucesso', 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao importar a planilha', 'error');
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleDeleteBatch = async () => {
    if (!result) return;
    const confirmed = window.confirm(
      `Isso vai apagar todos os ${result.totalsCriados} lançamentos do lote ${result.batchId}.\nAs entidades criadas (categorias, subcategorias, formas de pagamento) NÃO serão removidas.\n\nDeseja continuar?`,
    );
    if (!confirmed) return;

    try {
      const response = await api.deleteImportLancamentosBatch(result.batchId);
      toast(`${response.deleted} lançamentos removidos com sucesso`, 'success');
      setResult(null);
      setPreview(null);
      setFile(null);
      setResumed(false);
      setKeptDuplicateRowNumbers(new Set());
      setReviewedDuplicateRowNumbers(new Set());
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao desfazer importação', 'error');
    }
  };

  const handleConfirm = async () => {
    if (!preview) {
      toast('Gere o preview antes de confirmar', 'error');
      return;
    }

    setIsConfirming(true);
    try {
      // "Ignorar duplicatas potenciais" = nenhuma duplicata entra, então manda
      // a lista vazia. Sem ele, vale a decisão linha a linha.
      const response = await api.confirmImportLancamentos({
        batchPreviewId: preview.batchPreviewId,
        keepDuplicateRowNumbers: skipAllDuplicates ? [] : Array.from(keptDuplicateRowNumbers),
        resolutions: { subCategoryTypes },
        mapping,
      });
      setResult(response);
      setEtapa(4);
      limparProgresso(preview.batchPreviewId);
      setKeptDuplicateRowNumbers(new Set());
      setReviewedDuplicateRowNumbers(new Set());
      toast('Importação concluída', 'success');
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao confirmar importação', 'error');
    } finally {
      setIsConfirming(false);
    }
  };

  const handleToggleIgnoreRow = (rowNumber: number, ignore: boolean) => {
    setIgnoredRowNumbers((current) => {
      const next = new Set(current);
      if (ignore) next.add(rowNumber);
      else next.delete(rowNumber);
      return next;
    });
  };

  const handleToggleKeepDuplicate = (rowNumber: number, keep: boolean) => {
    setKeptDuplicateRowNumbers((current) => {
      const next = new Set(current);
      if (keep) next.add(rowNumber);
      else next.delete(rowNumber);
      return next;
    });
    setReviewedDuplicateRowNumbers((current) => {
      if (current.has(rowNumber)) return current;
      const next = new Set(current);
      next.add(rowNumber);
      return next;
    });
  };

  const handleStartNewImport = () => {
    if (preview) limparProgresso(preview.batchPreviewId);
    setFile(null);
    setPreview(null);
    setResult(null);
    setResumed(false);
    setEtapa(0);
    setEscolhasGrupos({});
    setEscolhasCategorias({});
    setKeptDuplicateRowNumbers(new Set());
    setReviewedDuplicateRowNumbers(new Set());
    setSkipAllDuplicates(false);
    setIgnoredRowNumbers(new Set());
  };

  const handleBackToUpload = () => {
    if (preview) limparProgresso(preview.batchPreviewId);
    setPreview(null);
    setResumed(false);
    setEtapa(0);
    setEscolhasGrupos({});
    setEscolhasCategorias({});
    setKeptDuplicateRowNumbers(new Set());
    setReviewedDuplicateRowNumbers(new Set());
    setSkipAllDuplicates(false);
    setIgnoredRowNumbers(new Set());
  };

  const handleRetryDiscardedRows = async (corrections: ImportRowCorrection[]) => {
    if (!preview) return;
    setIsFixingRows(true);
    try {
      const response = await api.fixImportLancamentosRows(preview.batchPreviewId, corrections);
      setPreview(response);
      // As linhas mudaram, entao a conciliacao foi recalculada e as chaves
      // antigas podem nao existir mais: as escolhas voltam para as sugestoes.
      setEscolhasGrupos({});
      setEscolhasCategorias({});
      const remaining = response.discardedRows.length + response.duplicateRows.length;
      toast(
        remaining === 0 ? 'Todas as linhas foram corrigidas' : `${remaining} linha(s) ainda precisam de atenção`,
        remaining === 0 ? 'success' : 'error',
      );
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao reprocessar linhas', 'error');
    } finally {
      setIsFixingRows(false);
    }
  };

  const handleDownloadDiscardedRows = async () => {
    if (!preview) return;
    setIsDownloadingErrors(true);
    try {
      await api.downloadImportLancamentosDiscardedRows(preview.batchPreviewId);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao baixar planilha de erros', 'error');
    } finally {
      setIsDownloadingErrors(false);
    }
  };

  const handleExportCsv = async () => {
    if (!preview || !result) {
      toast('Confirme a importação antes de exportar o relatório', 'error');
      return;
    }

    setIsExporting(true);
    try {
      const full = await api.getImportLancamentosRows(result.batchId);
      const ingestedRows = full.rows.filter((row: any) => !row.ignored);

      const lines: string[][] = [
        ['Metrica', 'Valor'],
        ['Batch ID', result.batchId],
        ['Total de linhas', String(full.summary.totalRows)],
        ['Total importado', String(result.totalsCriados)],
        ['Categorias criadas', String(result.entidadesCriadas.categorias)],
        ['Subcategorias criadas', String(result.entidadesCriadas.subCategorias)],
        ['Formas de pagamento criadas', String(result.entidadesCriadas.formasPagamento)],
        ['Subcategorias reclassificadas', String(result.subCategoriasReclassificadas ?? 0)],
        ['Duplicatas ignoradas', String(result.skippedDuplicates)],
        ['Linhas com erro', String(full.discardedRows.length)],
        [],
        ['Linha', 'Categoria', 'Subcategoria', 'Forma', 'Valor', 'Regra', 'Status'],
        ...ingestedRows.map((row: any) => [
          String(row.rowNumber),
          row.categoriaOriginal || '-',
          row.subCategoriaOriginal || '-',
          row.formaPagamentoOriginal || '-',
          String(row.valorOriginal ?? '-'),
          row.rule,
          row.conflict ? 'Conflito' : row.duplicate ? 'Duplicata' : 'Ok',
        ]),
      ];

      if (full.discardedRows.length > 0) {
        lines.push([]);
        lines.push(['--- Linhas Descartadas ---']);
        lines.push(['Linha', 'Subcategoria', 'Valor', 'Data', 'Motivo']);
        for (const row of full.discardedRows) {
          lines.push([
            String(row.rowNumber),
            row.subCategoriaOriginal || '-',
            String(row.valorOriginal ?? '-'),
            row.data ? row.data.slice(0, 10) : '-',
            row.motivo,
          ]);
        }
      }

      const csv = lines
        .map((line) => line.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(','))
        .join('\n');

      const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-importacao-${result.batchId}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao exportar CSV', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPdf = async () => {
    if (!preview || !result) {
      toast('Confirme a importação antes de exportar o relatório', 'error');
      return;
    }

    setIsExporting(true);
    try {
      const full = await api.getImportLancamentosRows(result.batchId);
      const ingestedRows = full.rows.filter((row: any) => !row.ignored);

      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(16);
      doc.text('Relatório de Importação XLSM', 14, 16);
      doc.setFontSize(10);
      doc.text(`Batch: ${result.batchId}`, 14, 24);
      doc.text(`Importados: ${result.totalsCriados} | Duplicatas ignoradas: ${result.skippedDuplicates} | Linhas com erro: ${full.discardedRows.length}`, 14, 30);

      autoTable(doc, {
        startY: 38,
        head: [['Linha', 'Categoria', 'Subcategoria', 'Forma', 'Valor', 'Regra', 'Status']],
        body: ingestedRows.map((row: any) => [
          String(row.rowNumber),
          row.categoriaOriginal || '-',
          row.subCategoriaOriginal || '-',
          row.formaPagamentoOriginal || '-',
          String(row.valorOriginal ?? '-'),
          row.rule,
          row.conflict ? 'Conflito' : row.duplicate ? 'Duplicata' : 'Ok',
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 118, 110] },
      });

      if (full.discardedRows.length > 0) {
        const finalY = (doc as any).lastAutoTable?.finalY ?? 80;
        doc.setFontSize(12);
        doc.text('Linhas Descartadas', 14, finalY + 10);
        autoTable(doc, {
          startY: finalY + 16,
          head: [['Linha', 'Subcategoria', 'Valor', 'Data', 'Motivo']],
          body: full.discardedRows.map((row) => [
            String(row.rowNumber),
            row.subCategoriaOriginal || '-',
            String(row.valorOriginal ?? '-'),
            row.data ? row.data.slice(0, 10) : '-',
            row.motivo,
          ]),
          styles: { fontSize: 8 },
          headStyles: { fillColor: [185, 28, 28] },
        });
      }

      doc.save(`relatorio-importacao-${result.batchId}.pdf`);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Erro ao exportar PDF', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const allDuplicatesReviewed = skipAllDuplicates || (preview
    ? preview.duplicateRows.every((row) => reviewedDuplicateRowNumbers.has(row.rowNumber))
    : true);
  // Cada linha com problema precisa ter sido corrigida (some da lista) ou
  // marcada como ignorada; duplicatas precisam de uma resposta explícita
  // (individual ou pelo "ignorar duplicatas potenciais").
  const allDiscardedResolved = !preview
    || preview.discardedRows.every((row) => ignoredRowNumbers.has(row.rowNumber));
  const resolutionComplete = !!preview && allDiscardedResolved && allDuplicatesReviewed;

  // --- Conciliação: itens, destinos disponíveis e o de-para que vai no confirm.
  const reconciliation = preview?.reconciliation;
  const opcoesDestino = preview?.opcoesDestino;

  const itensGrupos: ReconciliationItem[] = (reconciliation?.grupos ?? []).map((grupo) => ({
    key: grupo.key,
    planilhaNome: grupo.planilhaNome,
    tipo: grupo.tipo,
    sugestao: grupo.sugestao,
    detalhe: `${grupo.categoriasCount} subcategoria(s) · ${grupo.lancamentosCount} lançamento(s) identificado(s)`,
    filhos: (reconciliation?.categorias ?? [])
      .filter((categoria) => categoria.grupoKey === grupo.key)
      .map((categoria) => categoria.planilhaNome),
  }));

  const itensCategorias: ReconciliationItem[] = (reconciliation?.categorias ?? []).map((categoria) => ({
    key: categoria.key,
    planilhaNome: categoria.planilhaNome,
    tipo: categoria.tipo,
    sugestao: categoria.sugestao,
    secaoKey: categoria.grupoKey,
    detalhe: `${categoria.lancamentosCount} lançamento(s) · ${categoria.valorTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`,
  }));

  /** Destino de um grupo já decidido, para nomear a seção e filtrar as categorias. */
  const destinoDoGrupo = (grupoKey: string): { id: string | null; nome: string } => {
    const grupo = reconciliation?.grupos.find((item) => item.key === grupoKey);
    const escolha = escolhasGrupos[grupoKey];
    if (escolha?.destinoId) {
      const existente = opcoesDestino?.grupos.find((opcao) => opcao.id === escolha.destinoId);
      if (existente) return { id: existente.id, nome: existente.nome };
    }
    const novo = escolha?.novoNome?.trim();
    if (novo) return { id: null, nome: novo };
    return { id: grupo?.sugestao.id ?? null, nome: grupo?.sugestao.nome ?? '—' };
  };

  const secoesCategorias: ReconciliationSection[] = (reconciliation?.grupos ?? []).map((grupo) => ({
    key: grupo.key,
    titulo: grupo.planilhaNome ?? 'Sem grupo na planilha',
    subtitulo: `Grupo no FD: ${destinoDoGrupo(grupo.key).nome}`,
  }));

  const opcoesParaGrupo = (item: ReconciliationItem): ReconciliationOption[] =>
    (opcoesDestino?.grupos ?? []).filter((opcao) => opcao.tipo === item.tipo);

  // A categoria mora dentro de um grupo, então os destinos são os do grupo já
  // escolhido na etapa anterior. Grupo novo ainda não tem categoria nenhuma.
  const opcoesParaCategoria = (item: ReconciliationItem): ReconciliationOption[] => {
    const todas = (opcoesDestino?.categorias ?? []).filter((opcao) => opcao.tipo === item.tipo);
    if (!item.secaoKey) return todas;
    const grupo = destinoDoGrupo(item.secaoKey);
    if (!grupo.id) return [];
    return todas.filter((opcao) => opcao.grupoId === grupo.id);
  };

  const pendentesGrupos = itensGrupos.filter((item) => !isConciliado(item, escolhasGrupos[item.key])).length;
  const pendentesCategorias = itensCategorias.filter((item) => !isConciliado(item, escolhasCategorias[item.key])).length;

  // O `entitiesToCreate` do preview foi calculado antes de o usuário decidir
  // qualquer coisa, então na revisão ele mentiria sobre o que será criado.
  // Estes números saem das escolhas atuais, os mesmos das duas etapas.
  const contar = (itens: ReconciliationItem[], escolhas: Record<string, ReconciliationChoiceState>) =>
    itens.reduce(
      (acc, item) => {
        const situacao = situacaoDoItem(item, escolhas[item.key]);
        if (situacao === 'existente') acc.existentes += 1;
        else if (situacao === 'nova') acc.novas += 1;
        return acc;
      },
      { existentes: 0, novas: 0 },
    );

  const resumoGrupos = contar(itensGrupos, escolhasGrupos);
  const resumoCategorias = contar(itensCategorias, escolhasCategorias);

  const paraMapping = (escolhas: Record<string, ReconciliationChoiceState>) =>
    Object.entries(escolhas)
      .map(([key, escolha]) => ({ key, destinoId: escolha.destinoId, novoNome: escolha.novoNome?.trim() || undefined }))
      .filter((escolha) => escolha.destinoId || escolha.novoNome);

  const mapping: ImportReconciliationMapping = {
    grupos: paraMapping(escolhasGrupos),
    categorias: paraMapping(escolhasCategorias),
  };

  // Fase atual, usada tanto pelo indicador fixo no topo quanto pelo rodapé.
  const phase = result ? 4 : preview ? etapa : 0;
  const phaseLabels = ['Arquivo', 'Grupos', 'Categorias', 'Revisão', 'Concluído'];

  // Ação de recuo numa ponta, ação principal na outra. A <div/> vazia segura a
  // ponta esquerda quando não há botão de recuo, para o principal não escorregar.
  const [footerLeft, footerRight] = isResuming
    ? [null, null]
    : result
      ? [<div key="spacer" />, <Button key="done" type="button" onClick={onClose}>Concluir</Button>]
      : !preview
        ? [
          <Button key="cancel" type="button" variant="outline" onClick={onClose}>Cancelar</Button>,
          <Button key="preview" onClick={handlePreview} isLoading={isPreviewing} disabled={!file}>Ler planilha</Button>,
        ]
        : etapa === 1
          ? [
            <Button key="back" type="button" variant="outline" onClick={handleBackToUpload}>Voltar</Button>,
            <Button
              key="next"
              type="button"
              onClick={() => setEtapa(2)}
              disabled={pendentesGrupos > 0}
            >
              Continuar
            </Button>,
          ]
          : etapa === 2
            ? [
              <Button key="back" type="button" variant="outline" onClick={() => setEtapa(1)}>Voltar</Button>,
              <Button
                key="next"
                type="button"
                onClick={() => setEtapa(3)}
                disabled={pendentesCategorias > 0}
              >
                Continuar
              </Button>,
            ]
            : [
              <Button
                key="back"
                type="button"
                variant="outline"
                onClick={() => (itensGrupos.length > 0 ? setEtapa(2) : handleBackToUpload())}
              >
                Voltar
              </Button>,
              <Button key="confirm" onClick={handleConfirm} isLoading={isConfirming} disabled={!resolutionComplete}>
                Confirmar importação
              </Button>,
            ];

  const footer = isResuming ? null : (
    <div className={styles.footerBar}>
      {footerLeft}
      {footerRight}
    </div>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Importar planilha"
      size="xlarge"
      scrollBody
      footer={footer}
    >
      <div className={styles.content}>
        <div className={styles.stepper}>
          {phaseLabels.map((label, index) => (
            <div
              key={label}
              className={`${styles.stepItem} ${index === phase ? styles.stepItemActive : ''} ${index < phase ? styles.stepItemDone : ''}`}
            >
              <span className={styles.stepNum}>{index < phase ? '✓' : index + 1}</span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {/*
          * Fica fora das etapas de propósito: a retomada abre na conciliação, e
          * o botão de recomeçar só existia na revisão — ou seja, só aparecia
          * depois de percorrer o wizard inteiro.
          */}
        {resumed && preview && !result && (
          <div className={styles.resumedBanner}>
            <span>Retomando a revisão de <strong>{preview.fileName || 'uma importação pendente'}</strong> — seu progresso não foi perdido.</span>
            <Button type="button" variant="outline" onClick={handleStartNewImport}>Começar nova importação</Button>
          </div>
        )}

        {isResuming ? (
          <p className={styles.resumingNote}>Verificando importação pendente...</p>
        ) : result ? (
          <>
            <div className={styles.resultBox}>
              <FiCheckCircle size={22} />
              <div>
                <strong>{result.totalsCriados} lançamentos importados</strong>
                {result.skippedDuplicates > 0 && (
                  <span>{result.skippedDuplicates} duplicata(s) deixadas de fora</span>
                )}
                {result.dataMin && result.dataMax && (
                  <span className={styles.resultPeriod}>
                    Período: {new Date(result.dataMin).toLocaleDateString('pt-BR')} – {new Date(result.dataMax).toLocaleDateString('pt-BR')} — ajuste o filtro de ano na tela de lançamentos para visualizar.
                  </span>
                )}
              </div>
            </div>
            <div className={styles.exportActions}>
              <Button type="button" variant="secondary" onClick={handleExportCsv} isLoading={isExporting} disabled={isExporting}>Exportar CSV</Button>
              <Button type="button" variant="outline" onClick={handleExportPdf} isLoading={isExporting} disabled={isExporting}>Exportar PDF</Button>
              <Button type="button" variant="outline" onClick={handleDeleteBatch}>Desfazer importação</Button>
            </div>
          </>
        ) : !preview ? (
          <>
            <div className={styles.avisoPlanilha}>
              <FiCheckCircle size={18} />
              <div>
                <strong>Importe apenas a "Planilha de Controle Financeiro - LTQ Educação"</strong>
                <span>
                  É o único modelo que o Financeiro Diário sabe ler. Outro arquivo, mesmo em Excel, tende a vir
                  incompleto ou nem ser aceito.
                </span>
              </div>
            </div>
            <p className={styles.intro}>
              Aceita .xlsx, .xlsm (aba "Base de Dados") e .csv até 10 MB. O sistema tenta resolver tipo,
              subcategoria e forma de pagamento com base na planilha e no seu plano de contas.
            </p>
            <Card className={styles.panel}>
              <div className={styles.uploadBox}>
                <FiUploadCloud size={34} />
                <label className={styles.fileButton}>
                  Selecionar arquivo
                  <input
                    type="file"
                    accept=".xlsx,.xlsm,.csv"
                    hidden
                    onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                  />
                </label>
                <div className={styles.fileName}>{file ? file.name : 'Nenhum arquivo selecionado'}</div>
              </div>
            </Card>
          </>
        ) : etapa === 1 ? (
          <ImportReconciliationStep
            modo="grupos"
            itens={itensGrupos}
            opcoesPara={opcoesParaGrupo}
            escolhas={escolhasGrupos}
            onChange={(key, escolha) => setEscolhasGrupos((atual) => ({ ...atual, [key]: escolha }))}
          />
        ) : etapa === 2 ? (
          <ImportReconciliationStep
            modo="categorias"
            itens={itensCategorias}
            opcoesPara={opcoesParaCategoria}
            escolhas={escolhasCategorias}
            onChange={(key, escolha) => setEscolhasCategorias((atual) => ({ ...atual, [key]: escolha }))}
            secoes={secoesCategorias}
          />
        ) : (
          <>
            <div className={styles.counters}>
              <div><strong>{preview.summary.totalValidRows}</strong> linhas válidas</div>
              <div><strong>{preview.summary.totalIgnoredRows}</strong> Linhas com problemas</div>
              <div><strong>{preview.summary.totalDuplicates}</strong> Duplicatas Potenciais</div>
            </div>

            {itensGrupos.length > 0 ? (
              <div>
                <label className={styles.groupLabel}>O que a conciliação decidiu</label>
                <p className={styles.groupText}>
                  Grupos: <strong>{resumoGrupos.existentes}</strong> aproveitados do seu plano de contas,{' '}
                  <strong>{resumoGrupos.novas}</strong> a criar.
                  <br />
                  Categorias: <strong>{resumoCategorias.existentes}</strong> aproveitadas,{' '}
                  <strong>{resumoCategorias.novas}</strong> a criar.
                  {preview.entitiesToCreate.formasPagamento.length > 0 && (
                    <>
                      <br />
                      Formas de pagamento a criar: <strong>{preview.entitiesToCreate.formasPagamento.length}</strong>.
                    </>
                  )}
                </p>
              </div>
            ) : (
              preview.entitiesToCreate.categorias.length + preview.entitiesToCreate.subCategorias.length + preview.entitiesToCreate.formasPagamento.length > 0 && (
                <div>
                  <label className={styles.groupLabel}>Entidades que serão criadas</label>
                  <p className={styles.groupText}>
                    {preview.entitiesToCreate.categorias.length} categoria(s), {preview.entitiesToCreate.subCategorias.length} subcategoria(s), {preview.entitiesToCreate.formasPagamento.length} forma(s) de pagamento
                  </p>
                </div>
              )
            )}

            {(preview.indeterminate.length > 0 || preview.discardedRows.length > 0 || preview.duplicateRows.length > 0) && (
              <ImportResolutionWizard
                batchId={preview.batchPreviewId}
                opcoesDestino={preview.opcoesDestino}
                indeterminate={preview.indeterminate}
                subCategoryTypes={subCategoryTypes}
                onSetSubCategoryType={handleSetSubCategoryType}
                discardedRows={preview.discardedRows}
                onRetryDiscarded={handleRetryDiscardedRows}
                onDownloadDiscarded={handleDownloadDiscardedRows}
                retryingDiscarded={isFixingRows}
                downloadingDiscarded={isDownloadingErrors}
                duplicateRows={preview.duplicateRows}
                keptDuplicateRowNumbers={keptDuplicateRowNumbers}
                reviewedDuplicateRowNumbers={reviewedDuplicateRowNumbers}
                onToggleKeepDuplicate={handleToggleKeepDuplicate}
                skipAllDuplicates={skipAllDuplicates}
                onSkipAllDuplicatesChange={setSkipAllDuplicates}
                ignoredRowNumbers={ignoredRowNumbers}
                onToggleIgnoreRow={handleToggleIgnoreRow}
              />
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
